import { useRef, useState } from "react";
import { Box, Button, Chip, CircularProgress, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Skeleton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import html2canvas from "html2canvas";
import { useSnackbar } from "notistack";
import { downloadCSV } from "../../helpers/UtilityHelper";
import { CARD_SX, COLORS, SHADOW_HOVER, TYPE } from "./emsTheme";

const csvCell = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** A metric definition behind an (i): what it is, its denominator and date basis. */
export const InfoTip = ({ text, label = "About this metric" }) => (
    <Tooltip title={<Typography sx={{ fontSize: 12, lineHeight: 1.5 }}>{text}</Typography>} arrow enterTouchDelay={0}>
        <IconButton size="small" aria-label={label} sx={{ p: 0.25, color: COLORS.ink3 }}>
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
    </Tooltip>
);

/** Active filters (other than the date, which the page shows once) under a chart title. */
export const FilterChips = ({ chips }) => (chips && chips.length ? (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
        {chips.map((c) => (
            <Chip key={c} label={c} size="small"
                sx={{ height: 22, fontSize: 12, bgcolor: COLORS.purpleSoft, color: COLORS.purple, "& .MuiChip-label": { px: 1, textTransform: "none" } }} />
        ))}
    </Box>
) : null);

/** Shown in a card's header while the page is focused on one of its slices: names the slice and
 *  puts the full chart back in one click. */
export const ShowAllChip = ({ label, onClear }) => (
    <Box component="button" type="button" onClick={onClear} aria-label={`Remove focus ${label} and show all`}
        sx={{ all: "unset", display: "inline-flex", alignItems: "center", gap: 0.75, pl: 1.25, pr: 1, py: 0.4, borderRadius: "999px", cursor: "pointer",
            bgcolor: COLORS.purpleSoft, border: `1px solid ${COLORS.purple}40`, color: COLORS.purple, fontSize: 12, fontWeight: 700, maxWidth: 260,
            "&:hover": { bgcolor: "#E9E0F3" }, "&:focus-visible": { outline: `2px solid ${COLORS.purple}` } }}>
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</Box>
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, pl: 0.75, borderLeft: `1px solid ${COLORS.purple}40`, whiteSpace: "nowrap" }}>
            ✕ Show all
        </Box>
    </Box>
);

/** A small segmented control used inside card headers. */
export const Segmented = ({ value, onChange, options, label }) => (
    <ToggleButtonGroup size="small" exclusive value={value} onChange={(_, v) => v && onChange(v)} aria-label={label}
        sx={{ bgcolor: COLORS.ground, borderRadius: "8px", p: "2px", flexWrap: "wrap",
            "& .MuiToggleButton-root": { border: 0, borderRadius: "6px !important", px: 1.25, py: 0.25, fontSize: 12, fontWeight: 600, textTransform: "none", color: COLORS.ink3 },
            "& .Mui-selected": { bgcolor: `${COLORS.surface} !important`, color: `${COLORS.purple} !important`, boxShadow: "0 1px 2px rgba(31,41,55,.12)" } }}>
        {options.map((o) => <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>)}
    </ToggleButtonGroup>
);

const StateBox = ({ height, children }) => (
    <Box sx={{ minHeight: Math.min(height, 200), display: "grid", placeItems: "center", textAlign: "center", px: 2 }}>{children}</Box>
);

/**
 * Card for one chart: a plain-label eyebrow (what this is), an insight title (what it says), and
 * the chart. Table view, CSV and PNG export live in one "more" menu so the header stays quiet.
 * Handles loading, empty, error (with Retry), the updated stamp and a partial-period note.
 *
 * table: { columns: [{ key, label, align, text(row) }], rows } for the table view and CSV.
 */
const ChartCard = ({
    eyebrow, title, subtitle, info, chips, height = 240, loading, error, onRetry, empty, emptyHint,
    updatedLabel, partialNote, table, fileName = "ems-chart", ariaSummary, actions, children,
}) => {
    const ref = useRef(null);
    const [view, setView] = useState("chart");
    const [menuAt, setMenuAt] = useState(null);
    const [exporting, setExporting] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const exportCsv = () => {
        if (!table) return;
        const lines = [table.columns.map((c) => csvCell(c.label)).join(",")];
        table.rows.forEach((r) => lines.push(table.columns.map((c) => csvCell(c.text ? c.text(r) : r[c.key])).join(",")));
        downloadCSV(lines.join("\n"), fileName);
    };
    const exportPng = async () => {
        if (!ref.current || exporting) return;
        setExporting(true);
        try {
            await renderPng();
        } catch (e) {
            enqueueSnackbar("Couldn't create the image. Try again.", { variant: "error" });
        } finally {
            setExporting(false);
        }
    };
    const renderPng = async () => {
        const canvas = await html2canvas(ref.current, {
            backgroundColor: "#FFFFFF", scale: 2, ignoreElements: (el) => el.dataset && el.dataset.noExport === "true",
            // html2canvas draws text a few pixels lower than the browser, so labels that clip their
            // overflow (ellipsis) lost their lower half in the image. In the copy it renders only,
            // let those labels show in full and wrap instead of being cut.
            onclone: (doc, el) => {
                const root = el || doc.body;
                root.querySelectorAll("*").forEach((n) => {
                    const cs = doc.defaultView.getComputedStyle(n);
                    if (cs.textOverflow === "ellipsis" || (cs.overflow === "hidden" && n.childElementCount === 0 && n.textContent.trim())) {
                        n.style.overflow = "visible";
                        n.style.textOverflow = "clip";
                        n.style.whiteSpace = "normal";
                    }
                });
            },
        });
        const link = document.createElement("a");
        link.href = canvas.toDataURL("image/png");
        link.download = `${fileName}.png`;
        link.click();
    };

    let body;
    if (loading) {
        body = <Skeleton variant="rounded" height={Math.min(height, 200)} sx={{ bgcolor: COLORS.purpleSoft }} aria-label="Loading chart" />;
    } else if (error) {
        body = (
            <StateBox height={height}>
                <Box sx={{ display: "grid", gap: 1, justifyItems: "center" }}>
                    <ErrorOutlineIcon sx={{ color: COLORS.unfavourable }} />
                    <Typography sx={{ fontSize: 14, color: COLORS.ink }}>Couldn't load this chart.</Typography>
                    {onRetry && <Button size="small" variant="outlined" onClick={onRetry} sx={{ textTransform: "none", borderColor: COLORS.purple, color: COLORS.purple }}>Retry</Button>}
                </Box>
            </StateBox>
        );
    } else if (empty) {
        body = (
            <StateBox height={height}>
                <Box sx={{ display: "grid", gap: 0.75, justifyItems: "center" }}>
                    <SearchOffOutlinedIcon sx={{ color: COLORS.context }} />
                    <Typography sx={{ fontSize: 14, color: COLORS.ink2 }}>{emptyHint || "Nothing to show for these filters."}</Typography>
                    <Typography sx={{ fontSize: 12, color: COLORS.ink3 }}>Try a longer date range or remove a filter.</Typography>
                </Box>
            </StateBox>
        );
    } else if (view === "table" && table) {
        body = (
            <Box sx={{ maxHeight: Math.max(height, 240), overflow: "auto" }}>
                <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13,
                    "& th": { position: "sticky", top: 0, bgcolor: COLORS.surface, textAlign: "left", fontSize: 12, fontWeight: 600, color: COLORS.ink3, py: 0.75, px: 1, borderBottom: `1px solid ${COLORS.grid}` },
                    "& td": { py: 0.75, px: 1, borderBottom: `1px solid ${COLORS.grid}`, color: COLORS.ink, fontVariantNumeric: "tabular-nums" },
                    "& .r": { textAlign: "right" } }}>
                    <thead><tr>{table.columns.map((c) => <th key={c.key} className={c.align === "right" ? "r" : undefined}>{c.label}</th>)}</tr></thead>
                    <tbody>{table.rows.map((r, i) => (
                        <tr key={r.key || i}>{table.columns.map((c) => <td key={c.key} className={c.align === "right" ? "r" : undefined}>{c.text ? c.text(r) : r[c.key]}</td>)}</tr>
                    ))}</tbody>
                </Box>
            </Box>
        );
    } else {
        body = <Box role="img" aria-label={ariaSummary || title}>{children}</Box>;
    }

    const ready = !loading && !error && !empty;
    return (
        <Box ref={ref} sx={{ ...CARD_SX, p: 2.5, display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 2, alignContent: "start", minWidth: 0, "& > *": { minWidth: 0 },
            transition: "box-shadow 200ms", "&:hover": { boxShadow: SHADOW_HOVER } }}>
            {/* The title keeps at least ~220px; narrower than that, the controls wrap under it. */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
                <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
                    {eyebrow && <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: COLORS.purple, mb: 0.5 }}>{eyebrow}</Typography>}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography component="h3" sx={{ ...TYPE.cardTitle, fontWeight: 700, color: COLORS.ink, textWrap: "balance" }}>{title}</Typography>
                        {info && <InfoTip text={info} />}
                    </Box>
                    {subtitle && <Typography sx={{ fontSize: 13, color: COLORS.ink3, mt: 0.25 }}>{subtitle}</Typography>}
                    <FilterChips chips={chips} />
                </Box>
                <Box data-no-export="true" sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {actions}
                    {exporting
                        ? <CircularProgress size={18} aria-label="Preparing image" sx={{ color: COLORS.purple, m: 0.5 }} />
                        : <Tooltip title="More"><IconButton size="small" aria-label="More options" onClick={(e) => setMenuAt(e.currentTarget)} sx={{ color: COLORS.ink3 }}><MoreHorizIcon fontSize="small" /></IconButton></Tooltip>}
                    <Menu anchorEl={menuAt} open={!!menuAt} onClose={() => setMenuAt(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
                        {table && (
                            <MenuItem onClick={() => { setView(view === "chart" ? "table" : "chart"); setMenuAt(null); }}>
                                <ListItemIcon>{view === "chart" ? <TableRowsOutlinedIcon fontSize="small" /> : <InsertChartOutlinedIcon fontSize="small" />}</ListItemIcon>
                                <ListItemText primaryTypographyProps={{ fontSize: 14 }}>{view === "chart" ? "Show as table" : "Show as chart"}</ListItemText>
                            </MenuItem>
                        )}
                        {table && (
                            <MenuItem disabled={!ready} onClick={() => { exportCsv(); setMenuAt(null); }}>
                                <ListItemIcon><FileDownloadOutlinedIcon fontSize="small" /></ListItemIcon>
                                <ListItemText primaryTypographyProps={{ fontSize: 14 }}>Download CSV</ListItemText>
                            </MenuItem>
                        )}
                        <MenuItem disabled={!ready || exporting} onClick={() => { setMenuAt(null); setTimeout(exportPng, 50); }}>
                            <ListItemIcon><ImageOutlinedIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ fontSize: 14 }}>Download image</ListItemText>
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>
            {body}
            {ready && (partialNote || updatedLabel) && (
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", fontSize: 12, color: COLORS.ink3 }}>
                    {partialNote && <span>{partialNote}</span>}
                    {updatedLabel && <span style={{ marginLeft: "auto" }}>{updatedLabel}</span>}
                </Box>
            )}
        </Box>
    );
};

export default ChartCard;
